import { Validates, ListValidationContext } from '@shopify/react-form';
import gettext from '@sdk/lang/gettext';
import validateTime from '@sdk/utils/validation/validateTime';
import { PersonaPickerOption } from '@sdk/components/forms/personaPickers/PersonaPicker/personaPicker.types';

import {
  BreakEntry,
  BreakEntryWithDates,
  TimeClockEntry,
  TimeClockEntryWithDates,
  BreaksValidationResult,
  FieldValues,
  ValidationResults,
  TimeClockSectionValidationResult,
} from '@web/timesheets/CreateTimesheetModal/createTimesheetModal.types';
import {
  isBeyondTimesheetRange,
  isBreakOverlappingWithOtherBreaks,
} from '@web/timesheets/CreateTimesheetModal/utils/breakUtils';
import {
  getTimeClockEntriesWithDates,
  getOverlappingTimeClockEntries,
} from '@web/timesheets/CreateTimesheetModal/utils/timeClockUtils';

/**
 * Form field validator for persona picker - makes sure field is required and allow for only 1 option to be selected.
 * Function return string if something is wrong and void if everything is all right.
 */
export const singleChoiceValidator = (value: PersonaPickerOption[]) => {
  if (!value || !value.length) {
    return gettext('Field is required');
  }
  if (value.length > 1) {
    return gettext('You can select only one option');
  }
};

/**
 * Form field validator for time field. As long as field is filled it need to be time value in order to be treated as valid.
 */
export const isValidTimeValue = (value: string) => {
  if (value && !validateTime(value)) {
    return gettext('Please provide valid time');
  }
};

/**
 * Validates input text to be time value (for example: "03:00:00" is passing while "abc" will fail).
 * Basic assumption is empty fields are allowed and therefore "" will be passing.
 *
 * @param breaks
 * @returns
 */
export const doesAllBreaksHaveProperTimeValues = (breaks: { start?: string; end?: string }[]) =>
  breaks.every((breakEntry) => {
    if (
      (!breakEntry.start || validateTime(breakEntry.start)) &&
      (!breakEntry.end || validateTime(breakEntry.end))
    ) {
      return true;
    }

    return false;
  });

/**
 * Form field validator that performs the basic check if all the entries all filled with start and end times.
 * You're only allowed to skip the last entry end time (on going break).
 *
 * It also checks, for every filled value, if it's the correct time value (and not a letters for example).
 *
 * Function return string if something is wrong and void if everything is all right.
 *
 * @param breaks BreakEntry[]
 */
export const breaksValidator = (breaks: BreakEntry[], { listItem }) => {
  if (listItem.deleted.value) {
    return;
  }

  if (breaks.length) {
    const nonDeletedBreaks = breaks.filter(({ deleted }) => !deleted);
    const lastBreakIndex = nonDeletedBreaks.length - 1;
    const { clockIn, clockOut } = listItem;
    const hasClockInClockOutTimes = Boolean(clockIn.value && clockOut.value);
    const hasRequiredStartEndTimes = nonDeletedBreaks.every((breakEntry, index) => {
      const canHaveEmptyEndValue = index === lastBreakIndex && !hasClockInClockOutTimes;

      return canHaveEmptyEndValue
        ? breakEntry.start?.length
        : breakEntry.start?.length && breakEntry.end?.length;
    });

    if (!hasRequiredStartEndTimes) {
      return gettext('Break entires are missing');
    }

    if (!doesAllBreaksHaveProperTimeValues(nonDeletedBreaks)) {
      return gettext('Break entires have wrong values');
    }
  }
};

/**
 * Clock out is required in every timeclock entry, except the last one.
 * If there's only one time clock entry than clock out is not required.
 *
 * Fields are also validatated to have proper time values.
 *
 * Function return string if something is wrong and void if everything is all right.
 *
 * @param clockOutTime HoursWithMinutesAndSeconds
 * @param context ValidationContext
 */
export const clockOutValidator: Validates<string, ListValidationContext<TimeClockEntry>> = (
  clockOutTime,
  { siblings }
) => {
  if (!clockOutTime) {
    const thereAreMoreMissingClockouts = siblings.some(({ clockOut }) => !clockOut.value?.length);
    if (thereAreMoreMissingClockouts) {
      return gettext('Clock out is required');
    }
  } else if (!validateTime(clockOutTime)) {
    return gettext('Break entires have wrong values');
  }
};

/**
 * Function validates multiple breaks within single time clock entry.
 * Breaks can not start before clock in and end before clock out.
 * When having multiple breaks one break can not start before previous break ends.
 *
 * @param timeClockEntry
 * @returns
 */
export const validateBreaks = (timeClockEntry: TimeClockEntryWithDates): BreaksValidationResult => {
  const { breaks } = timeClockEntry;
  const nonDeletedBreaks = breaks.filter(({ deleted }) => !deleted);
  const validationResult: BreaksValidationResult = {};
  let isBeyondRange = false;
  let isOverlap = false;

  nonDeletedBreaks.forEach((breakEntry: BreakEntryWithDates) => {
    isBeyondRange = isBeyondTimesheetRange(breakEntry, timeClockEntry);
    isOverlap = isBreakOverlappingWithOtherBreaks(breakEntry, nonDeletedBreaks);

    if (isBeyondRange && !isOverlap) {
      validationResult[breakEntry.key] = gettext('Breaks cannot go beyond timesheet time range');
    } else if (isBeyondRange && isOverlap) {
      validationResult[breakEntry.key] = gettext(
        'Breaks cannot overlap onto each other and cannot go beyond timesheet time range'
      );
    } else if (!isBeyondRange && isOverlap) {
      validationResult[breakEntry.key] = gettext('Breaks cannot overlap on each other');
    }
  });

  return validationResult;
};

/**
 * Extracted part of the create timesheet validation, that is responsible just for validating
 * time clock section. Validating means no clockIn / clockOut times are missing, breaks are not overlapping etc.
 *
 * @param timeClock TimeClockEntry[]
 * @param date Date
 * @returns
 */
export const validateTimeClockFormSection = (timeClock: TimeClockEntry[], date: Date) => {
  const validationResults: TimeClockSectionValidationResult = {};
  const nonDeletedTimeClock = timeClock.filter(({ deleted }) => !deleted);
  const hasAtLeastOneClockIn = nonDeletedTimeClock.some(({ clockIn }) => clockIn?.length);
  if (!hasAtLeastOneClockIn) {
    const timeClockEntryKey = nonDeletedTimeClock[0].key;
    validationResults.timeClock = {
      [timeClockEntryKey]: gettext('At least one time clock section is required'),
    };
  } else {
    const timeClockTimes = getTimeClockEntriesWithDates(nonDeletedTimeClock, date);
    const overlappingEntries = getOverlappingTimeClockEntries(timeClockTimes);

    if (Object.keys(overlappingEntries).length) {
      validationResults.timeClock = overlappingEntries;
    }

    const breaksValidationResults = {};
    timeClockTimes.forEach((timeClockEntry) => {
      const breakErrors = validateBreaks(timeClockEntry);
      if (Object.keys(breakErrors).length) {
        if (!breaksValidationResults[timeClockEntry.key]) {
          breaksValidationResults[timeClockEntry.key] = {};
        }
        breaksValidationResults[timeClockEntry.key] = { ...breakErrors };
      }
    });

    if (Object.keys(breaksValidationResults).length) {
      validationResults.breaks = breaksValidationResults;
    }
  }

  return validationResults;
};

/**
 * Function handles validation cross entries, like timesheet that can't overlap with each other,
 * breaks that are scheduled inside the timeclock boundaries and so on.
 */
export const validateCreateTimesheetForm = ({
  date,
  employee,
  location,
  position,
  timeClock,
}: FieldValues): ValidationResults => {
  const validationResults: ValidationResults = {};
  const [employeePicked] = employee;
  const [locationPicked] = location;
  const [positionPicked] = position;

  if (!date) {
    validationResults.date = gettext('Date is required');
  }

  if (!employeePicked?.id) {
    validationResults.employee = gettext('Employee is required');
  }

  if (!locationPicked?.id) {
    validationResults.location = gettext('Location is required');
  }

  if (!positionPicked?.id) {
    validationResults.position = gettext('Position is required');
  }

  return {
    ...validationResults,
    ...validateTimeClockFormSection(timeClock, date),
  };
};
